import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Field, formValueSelector } from "redux-form";
import { Grid, Icon, Message, Popup, Label } from "semantic-ui-react";
import { Images } from "../../../constants";
import { ConnectedInput } from "../input/ConnectedInput";
import { FormattedInput } from "../input/FormattedInput";
import { ConnectedRadioGroup } from "../radio/ConnectedRadioGroup";
import { required } from "../../../utilities/validationRules";
import {
  numbersToAlphabets,
  defaultClassObject,
} from "../../../utilities/customFunctions";
import { PlanTypeList } from "../company-enrolment/sampleData";
import _ from "lodash";

const ConnectedClassFields = connect((state, ownProps) => {
  const { formName = "FullCompanyEnrolmentPDV" } = ownProps;
  const selector = formValueSelector(formName);
  const employeeClass = selector(state, "employeeClass") || [];

  return {
    employeeClass,
  };
})(
  ({
    employeeClass,
    fields,
    styles,
    programTypeId,
    handleAllocationBlur,
    onRemoveClass,
    onAddClass,
  }) => {
    const [showAutoFillMessage, setShowAutoFillMessage] = useState({});
    const [activeAutoFillTimer, setActiveAutoFillTimer] = useState({});

    const handleFieldBlur = (index, purseType, allocType) => {
      handleAllocationBlur(index, purseType, allocType);

      // Show auto-fill message for dependent fields
      if (allocType === "allocationSingle") {
        const key = `${index}-${purseType}`;

        // Clear existing timer if any
        if (activeAutoFillTimer[key]) {
          clearTimeout(activeAutoFillTimer[key]);
        }

        // Show message
        setShowAutoFillMessage((prev) => ({ ...prev, [key]: true }));

        // Set new timer and store its reference
        const timerId = setTimeout(() => {
          setShowAutoFillMessage((prev) => ({ ...prev, [key]: false }));
          setActiveAutoFillTimer((prev) => {
            const newTimers = { ...prev };
            delete newTimers[key];
            return newTimers;
          });
        }, 5000);

        setActiveAutoFillTimer((prev) => ({ ...prev, [key]: timerId }));
      }
    };

    return (
      <div>
        {fields.map((employeeClassField, index) => {
          const currentField = employeeClass[index] || {};

          // Helper function to parse formatted currency values
          const parseAllocationValue = (value) => {
            if (!value) return 0;
            const cleaned = String(value).replace(/,/g, "");
            const parsed = parseFloat(cleaned);
            return isNaN(parsed) ? 0 : parsed;
          };

          const hsaSingle = parseAllocationValue(
            currentField?.allocation?.HSA?.allocationSingle
          );
          const wsaSingle = parseAllocationValue(
            currentField?.allocation?.WSA?.allocationSingle
          );
          const flexSingle = parseAllocationValue(
            currentField?.allocation?.Flex?.allocationSingle
          );

          const hsaOrWsaHasValue = hsaSingle > 0 || wsaSingle > 0;
          const flexHasValue = flexSingle > 0;
          const hasConflict = hsaOrWsaHasValue && flexHasValue;

          return (
            <div
              key={index}
              className={`${styles.sectionContent} ${styles.employeeClassWrapper}`}
            >
              <div className={styles.inputContainer}>
                <div
                  className={`${styles.checkboxDropdownContainer} ${styles.checkboxActive}`}
                >
                  <div
                    className={styles.sectionTitle}
                    style={{ display: "flex", flexDirection: "row" }}
                  >
                    Class {numbersToAlphabets(`${index + 1}`)}
                    <div style={{ alignContent: "center" }}>
                      <Popup
                        style={{ maxWidth: "500px" }}
                        content={
                          <>
                            <strong>Please note:</strong>
                            <p>
                              <br />
                              According to CRA guidelines, each class must have
                              a consistent allocation amount and be defined
                              using objective criteria.
                            </p>
                          </>
                        }
                        trigger={
                          <Icon
                            name="info circle"
                            size="small"
                            color="grey"
                            style={{ marginLeft: "5px" }}
                          />
                        }
                      />
                    </div>
                  </div>
                  <div className={styles.InputContainer}>
                    <Field
                      component={ConnectedInput}
                      label="Class Name"
                      name={`${employeeClassField}.className`}
                      type="text"
                      max={30}
                      validate={[required]}
                      placeholder="Class Name"
                    />

                    <Field
                      component={ConnectedInput}
                      label="Class Description (maximum of 256 characters)"
                      name={`${employeeClassField}.note`}
                      type="text"
                      max={256}
                      validate={[required]}
                      placeholder="Class Description"
                    />

                    <div className={styles.standaloneLabel}>
                      Do you want funds to carry over to the following year, or
                      have a "use it or lose it" option for this class? (Plan
                      Type)
                    </div>
                    <ConnectedRadioGroup
                      name={`${employeeClassField}.type`}
                      options={PlanTypeList}
                      thinVerticalSpacing={true}
                      noBackground={true}
                    />

                    <div className={styles.sectionTitle}>
                      Enter the annual allocation for this class.
                    </div>

                    {hasConflict && (
                      <Message negative>
                        <Message.Header>Allocation Conflict</Message.Header>
                        <p>
                          You cannot allocate funds to both HSA/WSA and Flex
                          accounts at the same time. Please choose one or the
                          other. Only the most recently entered values will be
                          retained.
                        </p>
                      </Message>
                    )}

                    <Grid columns="equal">
                      {/* Header Row */}
                      <Grid.Row
                        verticalAlign="middle"
                        className={styles.gridRowNoPadding}
                      >
                        <Grid.Column>
                          <div className={styles.standaloneLabel}></div>
                        </Grid.Column>
                        {programTypeId.includes("hsa") && (
                          <Grid.Column>
                            <div className={styles.standaloneLabel}>HSA</div>
                          </Grid.Column>
                        )}
                        {programTypeId.includes("wsa") && (
                          <Grid.Column>
                            <div className={styles.standaloneLabel}>WSA</div>
                          </Grid.Column>
                        )}
                        {programTypeId === "hsa+wsa" && (
                          <Grid.Column>
                            <div className={styles.standaloneLabel}>Flex</div>
                          </Grid.Column>
                        )}
                      </Grid.Row>

                      {/* Single Row */}
                      <Grid.Row
                        verticalAlign="middle"
                        className={styles.gridRowNoPadding}
                      >
                        <Grid.Column>
                          <div className={styles.standaloneLabel}>Single</div>
                        </Grid.Column>

                        {programTypeId.includes("hsa") && (
                          <Grid.Column>
                            <Field
                              component={FormattedInput}
                              name={`${employeeClassField}.allocation.HSA.allocationSingle`}
                              type="text"
                              placeholder="0.00"
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              onBlur={() =>
                                handleFieldBlur(
                                  index,
                                  "HSA",
                                  "allocationSingle"
                                )
                              }
                              style={{
                                borderColor:
                                  hasConflict && hsaSingle > 0
                                    ? "red"
                                    : undefined,
                              }}
                            />
                          </Grid.Column>
                        )}
                        {programTypeId === "hsa+wsa" && (
                          <div
                            style={{
                              color: "gray",
                              paddingTop: 10,
                              fontSize: 18,
                            }}
                          >
                            +
                          </div>
                        )}
                        {programTypeId.includes("wsa") && (
                          <Grid.Column>
                            <Field
                              component={FormattedInput}
                              name={`${employeeClassField}.allocation.WSA.allocationSingle`}
                              type="text"
                              placeholder="0.00"
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              onBlur={() =>
                                handleFieldBlur(
                                  index,
                                  "WSA",
                                  "allocationSingle"
                                )
                              }
                              style={{
                                borderColor:
                                  hasConflict && wsaSingle > 0
                                    ? "red"
                                    : undefined,
                              }}
                            />
                          </Grid.Column>
                        )}
                        {programTypeId === "hsa+wsa" && (
                          <div
                            style={{
                              color: "gray",
                              alignItems: "center",
                              display: "flex",
                            }}
                          >
                            OR
                          </div>
                        )}

                        {programTypeId === "hsa+wsa" && (
                          <Grid.Column>
                            <Field
                              component={FormattedInput}
                              name={`${employeeClassField}.allocation.Flex.allocationSingle`}
                              type="text"
                              placeholder="0.00"
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              onBlur={() =>
                                handleFieldBlur(
                                  index,
                                  "Flex",
                                  "allocationSingle"
                                )
                              }
                              style={{
                                borderColor:
                                  hasConflict && flexSingle > 0
                                    ? "red"
                                    : undefined,
                              }}
                            />
                          </Grid.Column>
                        )}
                      </Grid.Row>

                      {/* Auto-fill notification - only show one at a time */}
                      {(showAutoFillMessage[`${index}-HSA`] ||
                        showAutoFillMessage[`${index}-WSA`] ||
                        showAutoFillMessage[`${index}-Flex`]) && (
                        <Grid.Row>
                          <Grid.Column width={16}>
                            <Label color="blue" size="small" pointing="below">
                              <Icon name="magic" />
                              Auto-populated: Single → Single +1 → Single +2.
                              Modify any field as needed.
                            </Label>
                          </Grid.Column>
                        </Grid.Row>
                      )}

                      {/* Single +1 Row */}
                      <Grid.Row
                        verticalAlign="middle"
                        className={styles.gridRowNoPadding}
                      >
                        <Grid.Column>
                          <div className={styles.standaloneLabel}>
                            Single +1
                            <Popup
                              content={
                                <React.Fragment>
                                  The Single, +1, and +2 fields are optional for
                                  adding extra funds for dependents. These
                                  fields are auto-filled from the Single field
                                  but can be modified.
                                </React.Fragment>
                              }
                              position="top center"
                              trigger={
                                <img
                                  src={Images.question}
                                  className={styles.tooltip}
                                />
                              }
                            />
                          </div>
                        </Grid.Column>
                        {programTypeId.includes("hsa") && (
                          <Grid.Column>
                            <Field
                              component={FormattedInput}
                              name={`${employeeClassField}.allocation.HSA.allocationSinglePlusOne`}
                              type="text"
                              placeholder="0.00"
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              onBlur={() =>
                                handleAllocationBlur(
                                  index,
                                  "HSA",
                                  "allocationSinglePlusOne"
                                )
                              }
                              style={{
                                borderColor:
                                  hasConflict &&
                                  parseAllocationValue(
                                    currentField?.allocation?.HSA
                                      ?.allocationSinglePlusOne
                                  ) > 0
                                    ? "red"
                                    : undefined,
                                backgroundColor: showAutoFillMessage[
                                  `${index}-HSA`
                                ]
                                  ? "rgba(33, 133, 208, 0.05)"
                                  : undefined,
                              }}
                            />
                          </Grid.Column>
                        )}
                        {programTypeId === "hsa+wsa" && (
                          <div
                            style={{
                              color: "gray",
                              paddingTop: 10,
                              fontSize: 18,
                            }}
                          >
                            +
                          </div>
                        )}
                        {programTypeId.includes("wsa") && (
                          <Grid.Column>
                            <Field
                              component={FormattedInput}
                              name={`${employeeClassField}.allocation.WSA.allocationSinglePlusOne`}
                              type="text"
                              placeholder="0.00"
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              onBlur={() =>
                                handleAllocationBlur(
                                  index,
                                  "WSA",
                                  "allocationSinglePlusOne"
                                )
                              }
                              style={{
                                borderColor:
                                  hasConflict &&
                                  parseAllocationValue(
                                    currentField?.allocation?.WSA
                                      ?.allocationSinglePlusOne
                                  ) > 0
                                    ? "red"
                                    : undefined,
                                backgroundColor: showAutoFillMessage[
                                  `${index}-WSA`
                                ]
                                  ? "rgba(33, 133, 208, 0.05)"
                                  : undefined,
                              }}
                            />
                          </Grid.Column>
                        )}
                        {programTypeId === "hsa+wsa" && (
                          <div
                            style={{
                              color: "gray",
                              alignItems: "center",
                              display: "flex",
                            }}
                          >
                            OR
                          </div>
                        )}

                        {programTypeId === "hsa+wsa" && (
                          <Grid.Column>
                            <Field
                              component={FormattedInput}
                              name={`${employeeClassField}.allocation.Flex.allocationSinglePlusOne`}
                              type="text"
                              placeholder="0.00"
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              onBlur={() =>
                                handleAllocationBlur(
                                  index,
                                  "Flex",
                                  "allocationSinglePlusOne"
                                )
                              }
                              style={{
                                borderColor:
                                  hasConflict &&
                                  parseAllocationValue(
                                    currentField?.allocation?.Flex
                                      ?.allocationSinglePlusOne
                                  ) > 0
                                    ? "red"
                                    : undefined,
                                backgroundColor: showAutoFillMessage[
                                  `${index}-Flex`
                                ]
                                  ? "rgba(33, 133, 208, 0.05)"
                                  : undefined,
                              }}
                            />
                          </Grid.Column>
                        )}
                      </Grid.Row>

                      {/* Single +2 Row */}
                      <Grid.Row
                        verticalAlign="middle"
                        className={styles.gridRowNoPadding}
                      >
                        <Grid.Column>
                          <div className={styles.standaloneLabel}>
                            Single +2
                            <Popup
                              content={
                                <React.Fragment>
                                  The Single, +1, and +2 fields are optional for
                                  adding extra funds for dependents. These
                                  fields are auto-filled from the Single field
                                  but can be modified.
                                </React.Fragment>
                              }
                              position="top center"
                              trigger={
                                <img
                                  src={Images.question}
                                  className={styles.tooltip}
                                />
                              }
                            />
                          </div>
                        </Grid.Column>
                        {programTypeId.includes("hsa") && (
                          <Grid.Column>
                            <Field
                              component={FormattedInput}
                              name={`${employeeClassField}.allocation.HSA.allocationSinglePlusTwo`}
                              type="text"
                              placeholder="0.00"
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              style={{
                                borderColor:
                                  hasConflict &&
                                  parseAllocationValue(
                                    currentField?.allocation?.HSA
                                      ?.allocationSinglePlusTwo
                                  ) > 0
                                    ? "red"
                                    : undefined,
                                backgroundColor: showAutoFillMessage[
                                  `${index}-HSA`
                                ]
                                  ? "rgba(33, 133, 208, 0.05)"
                                  : undefined,
                              }}
                            />
                          </Grid.Column>
                        )}
                        {programTypeId === "hsa+wsa" && (
                          <div
                            style={{
                              color: "gray",
                              paddingTop: 10,
                              fontSize: 18,
                            }}
                          >
                            +
                          </div>
                        )}
                        {programTypeId.includes("wsa") && (
                          <Grid.Column>
                            <Field
                              component={FormattedInput}
                              name={`${employeeClassField}.allocation.WSA.allocationSinglePlusTwo`}
                              type="text"
                              placeholder="0.00"
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              style={{
                                borderColor:
                                  hasConflict &&
                                  parseAllocationValue(
                                    currentField?.allocation?.WSA
                                      ?.allocationSinglePlusTwo
                                  ) > 0
                                    ? "red"
                                    : undefined,
                                backgroundColor: showAutoFillMessage[
                                  `${index}-WSA`
                                ]
                                  ? "rgba(33, 133, 208, 0.05)"
                                  : undefined,
                              }}
                            />
                          </Grid.Column>
                        )}
                        {programTypeId === "hsa+wsa" && (
                          <div
                            style={{
                              color: "gray",
                              alignItems: "center",
                              display: "flex",
                            }}
                          >
                            OR
                          </div>
                        )}

                        {programTypeId === "hsa+wsa" && (
                          <Grid.Column>
                            <Field
                              component={FormattedInput}
                              name={`${employeeClassField}.allocation.Flex.allocationSinglePlusTwo`}
                              type="text"
                              placeholder="0.00"
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              style={{
                                borderColor:
                                  hasConflict &&
                                  parseAllocationValue(
                                    currentField?.allocation?.Flex
                                      ?.allocationSinglePlusTwo
                                  ) > 0
                                    ? "red"
                                    : undefined,
                                backgroundColor: showAutoFillMessage[
                                  `${index}-Flex`
                                ]
                                  ? "rgba(33, 133, 208, 0.05)"
                                  : undefined,
                              }}
                            />
                          </Grid.Column>
                        )}
                      </Grid.Row>
                    </Grid>

                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginTop: "10px",
                        fontStyle: "italic",
                      }}
                    >
                      <Icon name="info circle" size="small" />
                      <strong>Note:</strong> Values cascade automatically -{" "}
                      <strong>Single</strong> copies to{" "}
                      <strong>Single +1</strong> and <strong>Single +2</strong>;
                      when you modify <strong>Single +1</strong>, it updates{" "}
                      <strong>Single +2</strong>. You can edit any field
                      independently.
                    </div>
                    <br />
                  </div>
                </div>
              </div>
              <div>
                {_.size(fields) !== 1 && (
                  <div className={styles.removeAdministratorWrapper}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onRemoveClass) {
                          onRemoveClass(index);
                        } else {
                          fields.remove(index);
                        }
                      }}
                      className={styles.removeAdministrator}
                    >
                      - Remove Class
                    </a>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (onAddClass) {
              onAddClass();
            } else {
              fields.push(defaultClassObject);
            }
          }}
          className={styles.addAdministrator}
        >
          + Add New Class
        </a>
      </div>
    );
  }
);

export const ClassFieldsRenderer = ({
  fields,
  styles,
  programTypeId,
  handleAllocationBlur,
  formName = "FullCompanyEnrolmentPDV",
  onRemoveClass,
  onAddClass,
}) => {
  return (
    <ConnectedClassFields
      fields={fields}
      styles={styles}
      programTypeId={programTypeId}
      handleAllocationBlur={handleAllocationBlur}
      formName={formName}
      onRemoveClass={onRemoveClass}
      onAddClass={onAddClass}
    />
  );
};

export default ClassFieldsRenderer;
